import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, ExternalLink, Upload, Eye, EyeOff, Star } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import SafeImage from "../../components/SafeImage";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const EMPTY = {
  slug: "",
  published: true,
  featured: false,
  cover_design_url: "",
  cover_photo_url: "",
  photo_focus: "center 20%",
  kicker: { fr: "Édition spéciale", en: "Special Edition" },
  name_title: { fr: "", en: "" },
  name_line1: "",
  name_line2: "",
  name_accent: "",
  subtitle_italic: { fr: "", en: "" },
  event_full_title: { fr: "", en: "" },
  event_short_title: "",
  event_subtitle_italic: { fr: "", en: "" },
  event_date_full: { fr: "", en: "" },
  event_place: "",
  event_dateline: { fr: "", en: "" },
  pillar1_title: { fr: "Coopération", en: "Cooperation" },
  pillar1_body: { fr: "", en: "" },
  pillar2_title: { fr: "Investissement", en: "Investment" },
  pillar2_body: { fr: "", en: "" },
  pillar3_title: { fr: "Développement", en: "Development" },
  pillar3_body: { fr: "", en: "" },
  article_kicker: { fr: "", en: "" },
  article_title: { fr: "", en: "" },
  article_photo_url: "",
  article_sections: [{ title: { fr: "", en: "" }, body: { fr: "", en: "" } }],
  closing_note: { fr: "", en: "" },
  author: "Réel Mambe Mola",
  highlight_title: { fr: "", en: "" },
  highlight_body: { fr: "", en: "" },
  promo_description: { fr: "", en: "" },
};

function BilingualField({ label, value, onChange, rows }) {
  const Input = rows ? "textarea" : "input";
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="grid md:grid-cols-2 gap-2">
        <Input
          className="gai-input resize-y"
          placeholder="FR"
          rows={rows || undefined}
          value={value?.fr || ""}
          onChange={(e) => onChange({ ...(value || {}), fr: e.target.value })}
        />
        <Input
          className="gai-input resize-y"
          placeholder="EN"
          rows={rows || undefined}
          value={value?.en || ""}
          onChange={(e) => onChange({ ...(value || {}), en: e.target.value })}
        />
      </div>
    </div>
  );
}

function SimpleField({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <input className="gai-input" type={type} placeholder={placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "cover");
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    } finally { setUploading(false); }
  };
  return (
    <div className="p-4 border" style={{ borderColor: "var(--gai-grey-200)" }}>
      <div className="overline mb-2">{label}</div>
      <div className="flex items-center gap-4 flex-wrap">
        {value && <SafeImage src={value} alt="" className="object-cover" style={{ width: 80, height: 100 }} />}
        <input className="gai-input flex-1 min-w-[220px]" placeholder="URL de l'image" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-url`} />
        <label className="gai-btn-outline text-xs cursor-pointer">
          <Upload size={12} /> {uploading ? "Envoi…" : "Uploader"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid={`${testid}-file`} />
        </label>
      </div>
    </div>
  );
}

function SectionsEditor({ value, onChange }) {
  const items = value || [];
  const update = (i, key) => (v) => onChange(items.map((it, j) => (j === i ? { ...it, [key]: v } : it)));
  return (
    <div className="space-y-3" data-testid="edition-sections">
      {items.map((it, i) => (
        <div key={i} className="p-4 border relative" style={{ borderColor: "var(--gai-grey-200)" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-[var(--gai-red)]">Intertitre {i + 1}{i < 2 ? " · à côté de la photo" : ""}</div>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-[var(--gai-red)]" aria-label="Retirer" data-testid={`edition-section-remove-${i}`}>
              <Trash2 size={13} />
            </button>
          </div>
          <BilingualField label="Intertitre" value={it.title} onChange={update(i, "title")} />
          <div className="mt-3"><BilingualField label="Paragraphe" value={it.body} onChange={update(i, "body")} rows={4} /></div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { title: { fr: "", en: "" }, body: { fr: "", en: "" } }])} className="gai-btn-outline text-xs" data-testid="edition-section-add">
        <Plus size={12} /> Ajouter un intertitre
      </button>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-7 pt-6 border-t" style={{ borderColor: "var(--gai-grey-200)" }}>
    <div className="overline text-[var(--gai-red)] mb-4">{title}</div>
    <div className="space-y-4">{children}</div>
  </div>
);

function EditionForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const slug = form.slug || slugify(`${form.name_line1} ${form.name_line2} ${form.name_accent}`);
      const payload = { ...form, slug };
      if (initial?.id) await api.put(`/special-editions/${initial.id}`, payload);
      else await api.post("/special-editions", payload);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur enregistrement");
    } finally { setBusy(false); }
  };


  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.72)" }}>
      <form onSubmit={submit} className="w-full max-w-5xl gai-card gai-sharp p-7 relative max-h-[94vh] overflow-y-auto" data-testid="edition-form">
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[var(--gai-grey-600)] hover:text-[var(--gai-red)]" aria-label="Fermer">
          <X size={18} />
        </button>
        <div className="overline mb-1">{initial?.id ? "Modifier" : "Nouvelle"}</div>
        <h3 className="serif text-2xl text-[var(--gai-black)] mb-5">Édition spéciale</h3>

        <Section title="Paramètres">
          <div className="grid md:grid-cols-3 gap-3">
            <SimpleField label="Slug (URL)" value={form.slug} onChange={set("slug")} placeholder="auto depuis le nom" />
            <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)] mt-6">
              <input type="checkbox" checked={form.published} onChange={(e) => set("published")(e.target.checked)} data-testid="edition-published" />
              <Eye size={13} /> Publié
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)] mt-6">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured")(e.target.checked)} data-testid="edition-featured" />
              <Star size={13} /> À la une (bannière Home)
            </label>
          </div>
        </Section>

        <Section title="Page 1 — Couverture">
          <ImageField label="Couverture finale (image complète conçue — prioritaire)" value={form.cover_design_url} onChange={set("cover_design_url")} testid="edition-cover-design" />
          <div className="text-xs text-[var(--gai-grey-500)]">Si aucune couverture finale n'est fournie, la couverture est générée automatiquement à partir des champs ci-dessous.</div>
          <BilingualField label="Kicker (ex. Édition spéciale)" value={form.kicker} onChange={set("kicker")} />
          <BilingualField label="Titre avant le nom (ex. Gouverneure de la province de Lualaba)" value={form.name_title} onChange={set("name_title")} />
          <div className="grid md:grid-cols-3 gap-3">
            <SimpleField label="Nom ligne 1" value={form.name_line1} onChange={set("name_line1")} placeholder="FIFI" />
            <SimpleField label="Nom ligne 2" value={form.name_line2} onChange={set("name_line2")} placeholder="MASUKA" />
            <SimpleField label="Nom ligne 3" value={form.name_accent} onChange={set("name_accent")} placeholder="SAINI" />
          </div>
          <BilingualField label="Bandeau rouge (ex. Le Lualaba à Porto)" value={form.subtitle_italic} onChange={set("subtitle_italic")} />
          <ImageField label="Photo principale" value={form.cover_photo_url} onChange={set("cover_photo_url")} testid="edition-cover-photo" />
          <SimpleField label="Cadrage photo (object-position)" value={form.photo_focus} onChange={set("photo_focus")} placeholder="center 20%" />
        </Section>

        <Section title="Événement">
          <BilingualField label="Titre complet (ex. 4e Forum Afrique–Portugal)" value={form.event_full_title} onChange={set("event_full_title")} />
          <div className="grid md:grid-cols-2 gap-3">
            <SimpleField label="Lieu (ville)" value={form.event_place} onChange={set("event_place")} placeholder="Porto" />
            <SimpleField label="Titre page 3 (ex. AFRIQUE x PORTUGAL)" value={form.event_short_title} onChange={set("event_short_title")} />
          </div>
          <BilingualField label="Dates (couverture, ex. 8 – 9 octobre 2026)" value={form.event_date_full} onChange={set("event_date_full")} />
          <BilingualField label="Ligne date page 3 (ex. Porto - 8 & 9 octobre 2026)" value={form.event_dateline} onChange={set("event_dateline")} />
          <BilingualField label="Sous-titre page 3 (ex. Deux jours de rencontres et d'opportunités)" value={form.event_subtitle_italic} onChange={set("event_subtitle_italic")} />
        </Section>

        <Section title="Page 2 — Article">
          <BilingualField label="Rubrique (ex. Coopération Afrique - Portugal)" value={form.article_kicker} onChange={set("article_kicker")} />
          <BilingualField label="Titre de l'article" value={form.article_title} onChange={set("article_title")} rows={2} />
          <ImageField label="Photo de l'article (sinon photo principale)" value={form.article_photo_url} onChange={set("article_photo_url")} testid="edition-article-photo" />
          <SectionsEditor value={form.article_sections} onChange={set("article_sections")} />
          <BilingualField label="Encadré final (fond crème)" value={form.closing_note} onChange={set("closing_note")} rows={2} />
          <SimpleField label="Signature (auteur)" value={form.author} onChange={set("author")} placeholder="Réel Mambe Mola" />
        </Section>

        <Section title="Page 3 — Le Forum (3 axes)">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-4 border" style={{ borderColor: "var(--gai-grey-200)" }}>
              <div className="overline mb-2 text-[var(--gai-red)]">Axe {i}</div>
              <BilingualField label="Titre" value={form[`pillar${i}_title`]} onChange={set(`pillar${i}_title`)} />
              <div className="mt-3">
                <BilingualField label="Description" value={form[`pillar${i}_body`]} onChange={set(`pillar${i}_body`)} rows={2} />
              </div>
            </div>
          ))}
          <BilingualField label="Temps fort — titre (ex. Gala Afrique-Portugal)" value={form.highlight_title} onChange={set("highlight_title")} />
          <BilingualField label="Temps fort — texte" value={form.highlight_body} onChange={set("highlight_body")} rows={3} />
        </Section>

        <Section title="Bannière promo (Home)">
          <BilingualField label="Description sur bannière Home" value={form.promo_description} onChange={set("promo_description")} rows={2} />
        </Section>

        <div className="flex justify-end gap-3 pt-5 mt-5 border-t" style={{ borderColor: "var(--gai-grey-200)" }}>
          <button type="button" onClick={onCancel} className="gai-btn-ghost text-xs">Annuler</button>
          <button disabled={busy} className="gai-btn-gold text-xs" data-testid="edition-submit">
            {busy ? "Enregistrement…" : (initial?.id ? "Enregistrer" : "Publier l'édition")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminSpecialEditions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    const r = await api.get("/admin/special-editions").catch(() => ({ data: [] }));
    setItems(r.data || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (ed) => {
    if (!window.confirm(`Supprimer l'édition « ${ed.name_line1} ${ed.name_accent} » ?`)) return;
    await api.delete(`/special-editions/${ed.id}`);
    refresh();
  };

  return (
    <div data-testid="page-admin-special-editions">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="overline mb-2">Éditions spéciales</div>
          <h1 className="serif text-4xl md:text-5xl text-[var(--gai-black)] leading-none">3 pages magazine premium</h1>
          <p className="mt-2 text-sm text-[var(--gai-grey-500)] max-w-2xl">
            Publiez une nouvelle édition spéciale (couverture + article + forum) selon la maquette 3 pages de l'édition Fifi Masuka Saini (couverture, article signé, forum). Chaque édition est accessible via `/edition-speciale/&lt;slug&gt;` et offre un export social Story 9:16 + Post 16:9.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="gai-btn-gold text-xs" data-testid="btn-new-edition">
          <Plus size={12} /> Nouvelle édition
        </button>
      </div>

      {loading && <div className="text-sm text-[var(--gai-grey-500)]">Chargement…</div>}

      {!loading && items.length === 0 && (
        <div className="gai-card gai-sharp p-10 text-center text-[var(--gai-grey-500)]">
          Aucune édition spéciale pour l'instant.
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((ed) => (
          <div key={ed.id} className="gai-card gai-sharp overflow-hidden flex flex-col" data-testid={`edition-card-${ed.id}`}>
            <SafeImage src={ed.cover_design_url || ed.cover_photo_url} alt="" className="w-full h-44 object-cover" style={{ width: "100%", height: 176, objectPosition: ed.cover_design_url ? "center top" : ed.photo_focus || "center 20%" }} />
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {ed.featured && <span className="overline text-[var(--gai-red)]"><Star size={10} className="inline" /> UNE</span>}
                {!ed.published && <span className="text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-grey-500)]">Brouillon</span>}
                <span className="text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-grey-500)]">/{ed.slug}</span>
              </div>
              <div className="serif text-base text-[var(--gai-black)] leading-tight line-clamp-2" translate="no">
                {ed.name_line1} {ed.name_line2} {ed.name_accent}
              </div>
              <div className="text-xs text-[var(--gai-grey-600)] italic mt-1">{(ed.subtitle_italic || {}).fr}</div>
              <div className="text-[0.7rem] mt-2 text-[var(--gai-grey-500)]">
                {(ed.event_full_title || {}).fr} · {(ed.event_dateline || {}).fr}
              </div>

              <div className="mt-4 pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--gai-grey-200)" }}>
                <Link to={`/edition-speciale/${ed.slug}`} target="_blank" className="gai-btn-ghost text-[0.7rem]" data-testid={`edition-view-${ed.id}`}>
                  <ExternalLink size={12} /> Voir
                </Link>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(ed)} className="gai-btn-ghost p-2" aria-label="Modifier" data-testid={`edition-edit-${ed.id}`}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(ed)} className="gai-btn-ghost p-2 text-[var(--gai-red)] hover:opacity-80" aria-label="Supprimer" data-testid={`edition-delete-${ed.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditionForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}
